import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchExportRecordsPage } from '@src/hooks/useProjectExportPoll';

/**
 * Export records of a project for the export history tab, newest first, one
 * page on open and another page each time the list is scrolled to the end.
 *
 * The rows live in redux, not here: the project-wide poll writes statuses into
 * them by record id, and a row it has to reach can sit on any page already
 * loaded. What stays local is the state of one request — whether a page is in
 * flight — which no one else reads.
 *
 * This hook never polls. A record still running is followed by
 * `useProjectExportPoll`, which asks about that record alone, so no page is
 * ever fetched twice.
 *
 * @param {{ projectId: string, enabled: boolean }} params `enabled` false means
 *   no request is made at all.
 */
const useExportHistory = ({ projectId, enabled }) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.project.exportRecords.items);
  const nextCursor = useSelector(
    (state) => state.project.exportRecords.nextCursor,
  );

  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(
    Boolean(enabled && projectId),
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Read synchronously: a scroll handler can fire many times before React
  // re-renders, and each of those would otherwise ask for the same page.
  const isLoadingMoreRef = useRef(false);
  const nextCursorRef = useRef(nextCursor);
  nextCursorRef.current = nextCursor;

  useEffect(() => {
    if (!enabled || !projectId) return undefined;

    let cancelled = false;
    setIsLoadingFirstPage(true);
    fetchExportRecordsPage(projectId, dispatch)
      .catch(() => {
        // A failed load shows what is already in the list, or the empty state:
        // the history is a secondary flow and a toast would distract from
        // exporting.
      })
      .finally(() => {
        if (!cancelled) setIsLoadingFirstPage(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, projectId, dispatch]);

  const loadMore = useCallback(async () => {
    if (!enabled || !projectId) return;
    if (isLoadingMoreRef.current || !nextCursorRef.current) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      await fetchExportRecordsPage(projectId, dispatch, {
        cursor: nextCursorRef.current,
      });
    } catch (error) {
      // Keep what is on screen; scrolling again retries.
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [enabled, projectId, dispatch]);

  return {
    items,
    // Rows already loaded stay on screen while the page is refreshed; the
    // spinner is for having nothing to show yet.
    isLoading: isLoadingFirstPage && !items.length,
    isLoadingMore,
    hasMore: Boolean(nextCursor),
    loadMore,
  };
};

export default useExportHistory;
